import React from 'react';
import { Link } from 'react-router-dom';
import HabboImage from '../HabboImage';
import { frameUrl, frameDoc, frameEntity, frameArticle } from './frameLinks';

const PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0,
    imageRendering: 'pixelated',
};

const CARD_FACTS = 5;
const CARD_BADGES = 12;
const CARD_LOOKS = 7;

function CardFact({ fact, query }) {
    const value = fact.value || '';

    const inner = (
        <>
            <span className="w-full shrink-0 text-[10px] uppercase tracking-[1px] text-gray-500 sm:w-[130px]">
                {fact.label}
            </span>
            <span className="min-w-0 flex-1 text-[13px] leading-snug text-gray-800">{value || fact.url}</span>
        </>
    );

    if (!fact.url) {
        return <div className="flex flex-col gap-[1px] py-[5px] sm:flex-row sm:gap-3">{inner}</div>;
    }

    const isInternal = fact.url.startsWith('/');

    return isInternal ? (
        <Link
            to={fact.url}
            className="flex flex-col gap-[1px] py-[5px] hover:bg-gray-50 sm:flex-row sm:gap-3"
        >
            {inner}
        </Link>
    ) : (
        <Link
            to={frameUrl(fact.url, query)}
            className="flex flex-col gap-[1px] py-[5px] hover:bg-gray-50 sm:flex-row sm:gap-3"
        >
            {inner}
        </Link>
    );
}

function StatPill({ label, value }) {
    if (!value) return null;

    return (
        <span className="flex items-baseline gap-1 border border-gray-200 bg-offwhite px-2 py-[2px]">
            <span className="font-['Jersey_10'] text-[15px] leading-none text-maroon">{value}</span>
            <span className="text-[10px] uppercase tracking-[1px] text-gray-500">{label}</span>
        </span>
    );
}

function MemberStats({ member }) {
    if (!member) return null;

    const stats = [
        ['Posts', member.posts],
        ['Comments', member.comments],
        ['Articles', member.articles],
        ['Achievements', member.achievements],
        ['Stamps', member.stamps],
        ['Friends', member.friends],
    ].filter(([, value]) => Number(value) > 0);

    if (stats.length === 0) return null;

    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="mb-2 text-[10px] uppercase tracking-[1px] text-gray-500">
                On Frankly Origins
            </p>
            <div className="flex flex-wrap gap-2">
                {stats.map(([label, value]) => (
                    <StatPill key={label} label={label} value={value} />
                ))}
            </div>
        </div>
    );
}

function SkillStrip({ skills }) {
    if (!skills || skills.length === 0) return null;

    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="mb-2 text-[10px] uppercase tracking-[1px] text-gray-500">
                In game on Habbo Origins
            </p>
            <div className="flex flex-wrap gap-2">
            {skills.map(skill => (
                <span
                    key={skill.skill}
                    className="flex items-baseline gap-1 border border-gray-200 bg-offwhite px-2 py-[2px]"
                    title={skill.checkedAt ? `Last checked ${new Date(skill.checkedAt).toLocaleDateString('en-GB')}` : undefined}
                >
                    <span className="text-[10px] uppercase tracking-[1px] text-gray-500">{skill.skill}</span>
                    <span className="font-['Jersey_10'] text-[15px] leading-none text-maroon">{skill.experienceLabel}</span>
                    <span className="text-[10px] uppercase tracking-[1px] text-gray-500">xp</span>
                </span>
                ))}
            </div>
        </div>
    );
}

function BadgeStrip({ badges }) {
    if (!badges || badges.length === 0) return null;

    const shown = badges.slice(0, CARD_BADGES);
    const extra = badges.length - shown.length;

    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-[10px] uppercase tracking-[1px] text-gray-500">
                Badges Frank has spotted
            </p>
            <p className="mb-2 text-[11px] text-gray-500">
                Only ones seen being worn, so this is never the full set.
            </p>
            <div className="flex flex-wrap items-center gap-2">
                {shown.map(badge => (
                    <img
                        key={badge.code}
                        src={`${process.env.PUBLIC_URL}/img/badges/${badge.code}.png`}
                        alt={badge.name || badge.code}
                        title={badge.description || badge.name || badge.code}
                        draggable={false}
                        style={PIXEL_IMAGE}
                    />
                ))}
                {extra > 0 && (
                    <span className="text-[11px] text-gray-500">and {extra} more spotted</span>
                )}
            </div>
        </div>
    );
}

function LookStrip({ looks }) {
    if (!looks || looks.length === 0) return null;

    const shown = looks.slice(0, CARD_LOOKS);
    const extra = looks.length - shown.length;

    return (
        <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="mb-2 text-[10px] uppercase tracking-[1px] text-gray-500">
                Looks Frank has spotted
            </p>
            <div className="flex flex-wrap items-end gap-2">
                {shown.map((look, index) => (
                    <span key={`${look.figureString}-${index}`} className="bg-offwhite px-1">
                        <HabboImage
                            figure={look.figureString}
                            direction={4}
                            head_direction={4}
                            size="a"
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                            style={PIXEL_IMAGE}
                        />
                    </span>
                ))}
                {extra > 0 && (
                    <span className="text-[11px] text-gray-500">and {extra} more spotted</span>
                )}
            </div>
        </div>
    );
}

function FranklyNetEntityCard({ entity, query }) {
    if (!entity) return null;

    const facts = (entity.facts || []).slice(0, CARD_FACTS);
    const hasMore = (entity.facts || []).length > CARD_FACTS
        || (entity.mentions || []).length > 0
        || (entity.media || []).length > 0
        || (entity.context || []).length > 0
        || (entity.habbo?.badges || []).length > CARD_BADGES
        || (entity.habbo?.looks || []).length > CARD_LOOKS
        || Boolean(entity.body);

    return (
        <div className="mt-5 border border-gray-300 bg-white shadow-[0_3px_0_0_rgba(0,0,0,0.25)]">
            <div className="flex items-center gap-2 border-b border-gray-200 bg-offwhite px-4 py-2">
                <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-500">
                    Frank&apos;s card
                </span>
                <span className="bg-maroon px-2 py-[1px] text-[10px] font-bold uppercase text-white">
                    {entity.subtypeLabel || entity.kindLabel}
                </span>
                {entity.protected && (
                    <span
                        className="text-[10px] uppercase tracking-[1px] text-gray-400"
                        title="Frank only shows what the desk has approved for this one"
                    >
                        Curated only
                    </span>
                )}
                {entity.furni?.available === false && (
                    <span
                        className="text-[10px] uppercase tracking-[1px] text-gray-400"
                        title={entity.furni.removeDateLabel ? `Left the Catalogue on ${entity.furni.removeDateLabel}` : undefined}
                    >
                        Out of the catalogue
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
                {entity.imageUrl ? (
                    <div className="flex shrink-0 items-start justify-center bg-offwhite p-2 sm:justify-start">
                        <img
                            src={entity.imageUrl}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                            style={entity.kind === 'furni' ? PIXEL_IMAGE : { maxWidth: '120px', height: 'auto' }}
                        />
                    </div>
                ) : entity.avatar ? (
                    <div className="flex shrink-0 items-start justify-center bg-offwhite p-2 sm:justify-start">
                        <HabboImage
                            habbo={entity.avatar.habbo}
                            hotel={entity.avatar.hotel}
                            figure={entity.avatar.figure}
                            size="b"
                            direction={2}
                            head_direction={3}
                            alt=""
                            aria-hidden="true"
                            draggable={false}
                            style={PIXEL_IMAGE}
                        />
                    </div>
                ) : null}

                <div className="min-w-0 flex-1">
                    <p className="font-['Jersey_10'] text-[26px] uppercase leading-none tracking-[1px] text-maroon">
                        {entity.name}
                    </p>

                    {entity.headline && (
                        <p className="mt-[2px] text-[12px] uppercase tracking-[1px] text-gray-500">{entity.headline}</p>
                    )}

                    {entity.summary ? (
                        <p className="mt-2 text-[14px] leading-relaxed text-gray-800">{entity.summary}</p>
                    ) : entity.definition?.text && (
                        <p className="mt-2 text-[14px] leading-relaxed text-gray-800">{entity.definition.text}</p>
                    )}

                    {entity.origins?.motto && (
                        <p className="mt-2 text-[13px] italic leading-snug text-gray-600">
                            &ldquo;{entity.origins.motto}&rdquo;
                        </p>
                    )}

                    {facts.length > 0 && (
                        <div className="mt-3 flex flex-col divide-y divide-gray-100 border-t border-gray-100">
                            {facts.map((fact, index) => (
                                <CardFact key={`${fact.label}-${index}`} fact={fact} query={query} />
                            ))}
                        </div>
                    )}

                    <SkillStrip skills={entity.skills} />

                    <MemberStats member={entity.member} />

                    <BadgeStrip badges={entity.habbo?.badges} />

                    <LookStrip looks={entity.habbo?.looks} />

                    {entity.profiles?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {entity.profiles.map(profile => (
                                <Link
                                    key={profile.url}
                                    to={frameUrl(profile.url, query)}
                                    className="flex items-center gap-1 border border-gray-300 bg-gray-100 px-2 py-[3px] text-[10px] uppercase tracking-[1px] text-maroon hover:bg-gray-200"
                                >
                                    {profile.faviconUrl && (
                                        <img src={profile.faviconUrl} alt="" aria-hidden="true" width={12} height={12} />
                                    )}
                                    {profile.sourceName} profile
                                </Link>
                            ))}
                        </div>
                    )}

                    {entity.related?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {entity.related.slice(0, 4).map(record => (
                                <Link
                                    key={record.pageId}
                                    to={record.articleSlug
                                        ? frameArticle({ slug: record.articleSlug, habboId: record.habboId }, query)
                                        : frameUrl(record.url, query)}
                                    className="flex items-center gap-1 border border-gray-300 bg-gray-100 px-2 py-[3px] text-[10px] uppercase tracking-[1px] text-maroon hover:bg-gray-200"
                                >
                                    {record.faviconUrl && (
                                        <img src={record.faviconUrl} alt="" aria-hidden="true" width={12} height={12} />
                                    )}
                                    {record.sourceName}
                                </Link>
                            ))}
                        </div>
                    )}

                    {entity.media?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {entity.media.slice(0, 3).map((item, index) => (
                                <img
                                    key={`${item.imageUrl}-${index}`}
                                    src={item.imageUrl}
                                    alt={item.caption || ''}
                                    title={item.caption || ''}
                                    className="h-[74px] w-auto border border-gray-200 bg-offwhite"
                                />
                            ))}
                        </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                        {hasMore && entity.slug && (
                            <Link
                                to={frameEntity(entity.slug, query)}
                                className="border border-black/30 bg-maroon px-3 py-[5px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.4)' }}
                            >
                                Open the full card
                            </Link>
                        )}

                        {entity.linkUrl && (
                            <Link
                                to={entity.linkUrl.startsWith('/')
                                    ? frameDoc(entity.linkUrl, query)
                                    : frameUrl(entity.linkUrl, query)}
                                className="border border-gray-300 bg-white px-3 py-[5px] text-[12px] uppercase text-maroon hover:bg-gray-100"
                            >
                                {entity.linkLabel || (entity.linkUrl.startsWith('/') ? 'See Frankly Profile' : 'Open the source')}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FranklyNetEntityCard;
